import OpenAI from 'openai';
import { supabase } from '../lib/supabase';

interface AssistantConfig {
  id: string;
  nombre: string;
  modelo: string;
  instructions?: string;
  temperatura: number;
  max_tokens: number;
  top_p: number;
  frequency_penalty: number;
  presence_penalty: number;
  stop_sequences?: string[];
  tools?: any[];
  openai_assistant_id?: string;
  api_configuracion_id: string;
  vector_store_id?: string;
  vector_table_id?: string;
}

class OpenAIAssistantsService {
  private assistants: Map<string, AssistantConfig> = new Map();
  private openaiClients: Map<string, OpenAI> = new Map();
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY = 1000;
  private readonly TIMEOUT = 60000;
  private recursionCount = 0;
  private readonly MAX_RECURSION = 5;

  private getOpenAIClient(apiKey: string, organizationId?: string): OpenAI {
    const clientKey = `${apiKey}-${organizationId || ''}`;
    if (!this.openaiClients.has(clientKey)) {
      const client = new OpenAI({
        apiKey: apiKey.startsWith('sk-') ? apiKey : `sk-${apiKey}`,
        organization: organizationId,
        dangerouslyAllowBrowser: true,
        timeout: this.TIMEOUT,
        maxRetries: this.MAX_RETRIES,
        defaultHeaders: {
          'OpenAI-Beta': 'assistants=v2'
        }
      });
      this.openaiClients.set(clientKey, client);
    }
    return this.openaiClients.get(clientKey)!;
  }

  private async proxyRequest(endpoint: string, config: { apiKey: string; organizationId?: string }, body: any, method: string = 'POST'): Promise<any> {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseKey) {
      throw new Error('Missing Supabase configuration');
    }

    let retries = 0;
    while (retries < this.MAX_RETRIES) {
      try {
        console.log(`Making ${method} request to ${endpoint} via proxy`);
        
        // Determine if this is a file upload request
        const isFileUpload = endpoint.includes('/v1/files') && 
                            !endpoint.includes('/content') && 
                            method === 'POST' && 
                            body && body.file;
        
        const response = await fetch(`${supabaseUrl}/functions/v1/openai-proxy`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${supabaseKey}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            apiKey: config.apiKey,
            organizationId: config.organizationId,
            endpoint,
            body: method === 'GET' ? null : body,
            method,
            isFileUpload
          })
        });

        if (!response.ok) {
          let errorData;
          try {
            errorData = await response.json();
          } catch (e) {
            errorData = { error: `HTTP error! status: ${response.status}` };
          }
          
          console.error('Proxy request failed:', errorData);
          throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
        }

        const responseData = await response.json();
        console.log(`Proxy request successful for ${endpoint}`);
        return responseData;
      } catch (error) {
        console.error(`Attempt ${retries + 1} failed:`, error);
        retries++;
        if (retries === this.MAX_RETRIES) throw error;
        
        // Exponential backoff
        const delay = this.RETRY_DELAY * Math.pow(2, retries - 1);
        console.log(`Retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    throw new Error('Max retries exceeded');
  }

  async getAssistantConfig(assistantId: string): Promise<AssistantConfig> {
    try {
      console.log('Getting assistant config for:', assistantId);

      if (this.assistants.has(assistantId)) {
        return this.assistants.get(assistantId)!;
      }

      const { data: assistant, error } = await supabase
        .from('asistentes')
        .select('*, api_configuracion:api_configuraciones(*)')
        .eq('id', assistantId)
        .single();

      if (error) throw new Error(`Error al obtener el asistente: ${error.message}`);
      if (!assistant) throw new Error('Asistente no encontrado');
      if (!assistant.api_configuracion_id) throw new Error('Configuración de API no asignada');

      this.assistants.set(assistantId, assistant);
      return assistant;
    } catch (error: any) {
      console.error('Assistant Config Error:', error);
      throw new Error(`Error al obtener la configuración del asistente: ${error.message}`);
    }
  }

  async getConfig(configId: string): Promise<{ apiKey: string; organizationId?: string }> {
    try {
      const { data: config, error } = await supabase
        .from('api_configuraciones')
        .select('*')
        .eq('id', configId)
        .single();

      if (error) throw error;
      if (!config) throw new Error('Configuración no encontrada');

      return {
        apiKey: config.api_key.startsWith('sk-') ? config.api_key : `sk-${config.api_key}`,
        organizationId: config.organization_id || undefined
      };
    } catch (error: any) {
      throw new Error(`Error al obtener la configuración de API: ${error.message}`);
    }
  }

  async generateResponse(
    assistantId: string,
    messages: { role: 'user' | 'assistant' | 'system'; content: string; files?: File[] }[],
    files?: File[],
    use_code_interpreter: boolean = false,
    existing_thread_id?: string
  ): Promise<{ response: string; openai_thread_id: string; openai_message_id: string; file_ids?: string[] }> {
    try {
      this.recursionCount++;
      if (this.recursionCount > this.MAX_RECURSION) {
        this.recursionCount = 0;
        throw new Error('Recursion limit exceeded');
      }

      console.log('Generating response for assistant:', assistantId);
      const assistant = await this.getAssistantConfig(assistantId);
      const config = await this.getConfig(assistant.api_configuracion_id);
      const validMessages = messages.filter(msg => 
        msg.content && typeof msg.content === 'string' && msg.content.trim() !== ''
      );

      if (validMessages.length === 0) {
        throw new Error('No hay mensajes válidos para procesar');
      }

      // Determinar si usamos el cliente directo o el proxy
      // Probamos el cliente directo primero
      try {
        const client = this.getOpenAIClient(config.apiKey, config.organizationId);
        
        // Crear o usar thread existente
        let threadId = existing_thread_id;
        if (!threadId) {
          const thread = await client.beta.threads.create();
          threadId = thread.id;
          console.log('Created new thread:', threadId);
        } else {
          console.log('Using existing thread:', threadId);
        }
        
        // Subir archivos si existen
        const uploadedFiles = files?.length
          ? await Promise.all(
              files.map(async (file) => {
                return await client.files.create({ file, purpose: 'assistants' });
              })
            )
          : [];
        
        const fileIds = uploadedFiles.map(f => f.id);
        
        // Añadir mensajes al thread
        for (const msg of validMessages) {
          const isLast = msg === validMessages[validMessages.length - 1];
          await client.beta.threads.messages.create(threadId, {
            role: msg.role === 'system' ? 'user' : msg.role,
            content: msg.content,
            // Adjuntar archivos solo al último mensaje
            attachments: isLast && fileIds.length > 0
              ? fileIds.map(file_id => ({ 
                  file_id, 
                  tools: [{ 
                    type: use_code_interpreter ? 'code_interpreter' : 'file_search' 
                  }] 
                }))
              : undefined
          });
        }
        
        // Configurar opciones del run
        const runOptions: any = {
          assistant_id: assistant.openai_assistant_id
        };
        
        if (assistant.instructions) {
          runOptions.instructions = assistant.instructions;
        }
        
        if (use_code_interpreter) {
          runOptions.tools = [{ type: 'code_interpreter' }];
        }
        
        // Ejecutar
        const run = await client.beta.threads.runs.create(threadId, runOptions);
        console.log('Run created:', run.id);
        
        // Esperar a que complete
        const start = Date.now();
        let completedRun;
        do {
          if (Date.now() - start > this.TIMEOUT) throw new Error('Timeout waiting for response');
          await new Promise(r => setTimeout(r, 1000));
          completedRun = await client.beta.threads.runs.retrieve(threadId, run.id);
          console.log('Run status:', completedRun.status);
        } while (completedRun.status === 'queued' || completedRun.status === 'in_progress');
        
        if (completedRun.status === 'completed') {
          const messages = await client.beta.threads.messages.list(threadId);
          const last = messages.data[0];
          let content = '';
          
          for (const item of last.content) {
            if (item.type === 'text') {
              content += item.text.value + '\n';
            } else if (item.type === 'file') {
              try {
                const info = await client.files.retrieve(item.file.file_id);
                content += `\n[${info.filename}](${import.meta.env.VITE_SUPABASE_URL}/functions/v1/download-file?file_id=${item.file.file_id})\n`;
              } catch {
                content += '\nError al procesar archivo adjunto.\n';
              }
            }
          }
          
          if (!content.trim()) throw new Error('No se recibió una respuesta válida del asistente');
          
          this.recursionCount = 0;
          return {
            response: content.trim(),
            openai_thread_id: threadId,
            openai_message_id: last.id,
            // Importante: devolver los file_ids para su uso posterior
            file_ids: fileIds.length > 0 ? fileIds : undefined
          };
        }
        
        throw new Error(`Error en la ejecución del asistente: ${completedRun.status}`);
      } catch (clientError) {
        // Si falla el cliente directo, intentamos con el proxy
        console.warn('Error using direct client, falling back to proxy:', clientError);
        // Si llegamos aquí, el cliente OpenAI tuvo un problema, pero podemos intentar con el proxy
        
        // Crear o usar thread existente mediante proxy
        let threadId: string;
        if (existing_thread_id) {
          threadId = existing_thread_id;
          console.log('Using existing thread:', threadId);
        } else {
          console.log('Creating new thread');
          const thread = await this.proxyRequest(
            'https://api.openai.com/v1/threads',
            config,
            {},
            'POST'
          );
          threadId = thread.id;
          console.log('Created new thread:', threadId);
        }

        // Subir archivos mediante proxy si hay archivos
        let fileIds: string[] = [];
        if (files && files.length > 0) {
          console.log(`Uploading ${files.length} files via proxy`);
          
          for (const file of files) {
            try {
              console.log(`Uploading file: ${file.name}`);
              
              // Convertir archivo a base64
              const base64 = await this.fileToBase64(file);
              
              // Subir archivo usando la función edge
              const fileUploadData = {
                file: base64,
                name: file.name,
                type: file.type,
                purpose: 'assistants'
              };
              
              const uploadResult = await this.proxyRequest(
                'https://api.openai.com/v1/files',
                config,
                fileUploadData,
                'POST'
              );
              
              console.log('File uploaded with ID:', uploadResult.id);
              fileIds.push(uploadResult.id);
            } catch (error) {
              console.error('Error uploading file:', error);
              throw error;
            }
          }
        }

        // Añadir mensajes al thread
        for (const msg of validMessages) {
          const messageContent = {
            role: msg.role === 'system' ? 'user' : msg.role,
            content: msg.content,
          };

          console.log(`Adding message to thread: ${messageContent.role}`);
          await this.proxyRequest(
            `https://api.openai.com/v1/threads/${threadId}/messages`,
            config,
            messageContent,
            'POST'
          );
        }

        // Crear run con proxy
        console.log(`Creating run with assistant: ${assistant.openai_assistant_id}`);
        const runOptions: any = {
          assistant_id: assistant.openai_assistant_id || ""
        };

        if (assistant.instructions) {
          runOptions.instructions = assistant.instructions;
        }

        if (use_code_interpreter) {
          runOptions.tools = [{ type: "code_interpreter" }];
        }

        // Añadir file_ids al run si disponible, asegurándose de tener code_interpreter
        if (fileIds.length > 0) {
          runOptions.tools = runOptions.tools || [];
          if (!runOptions.tools.some((tool: any) => tool.type === 'code_interpreter')) {
            runOptions.tools.push({ type: 'code_interpreter' });
          }
          // Importante: aquí asignamos los file_ids al run
          runOptions.file_ids = fileIds;
        }

        const run = await this.proxyRequest(
          `https://api.openai.com/v1/threads/${threadId}/runs`,
          config,
          runOptions,
          'POST'
        );
        console.log('Run created:', run.id);

        // Esperar a que complete
        const start = Date.now();
        let completedRun;
        do {
          if (Date.now() - start > this.TIMEOUT) throw new Error('Timeout waiting for response');
          await new Promise(r => setTimeout(r, 1000));
          completedRun = await this.proxyRequest(
            `https://api.openai.com/v1/threads/${threadId}/runs/${run.id}`,
            config,
            null,
            'GET'
          );
          console.log('Run status:', completedRun.status);
        } while (completedRun.status === 'queued' || completedRun.status === 'in_progress');

        if (completedRun.status === 'completed') {
          // Obtener mensajes
          const messagesResponse = await this.proxyRequest(
            `https://api.openai.com/v1/threads/${threadId}/messages`,
            config,
            null,
            'GET'
          );
          
          const last = messagesResponse.data[0];
          let content = '';
          
          for (const item of last.content) {
            if (item.type === 'text') {
              content += item.text.value + '\n';
            } else if (item.type === 'file') {
              try {
                const info = await this.proxyRequest(
                  `https://api.openai.com/v1/files/${item.file.file_id}`,
                  config,
                  null,
                  'GET'
                );
                content += `\n[${info.filename}](${import.meta.env.VITE_SUPABASE_URL}/functions/v1/download-file?file_id=${item.file.file_id})\n`;
              } catch {
                content += '\nError al procesar archivo adjunto.\n';
              }
            }
          }
          
          if (!content.trim()) throw new Error('No se recibió una respuesta válida del asistente');
          
          this.recursionCount = 0;
          return {
            response: content.trim(),
            openai_thread_id: threadId,
            openai_message_id: last.id,
            file_ids: fileIds.length > 0 ? fileIds : undefined
          };
        }

        throw new Error(`Error en la ejecución del asistente: ${completedRun.status}`);
      }
    } catch (error: any) {
      this.recursionCount = 0;
      console.error('Error generating response:', error);
      throw error;
    }
  }

  // Método auxiliar para convertir File a base64
  private async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result as string;
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  }
  
  // Función para determinar el tipo MIME basado en la extensión del archivo
  private getMimeType(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    const mimeTypes: Record<string, string> = {
      'txt': 'text/plain',
      'html': 'text/html',
      'css': 'text/css',
      'js': 'application/javascript',
      'json': 'application/json',
      'pdf': 'application/pdf',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'svg': 'image/svg+xml',
      'mp3': 'audio/mpeg',
      'mp4': 'video/mp4',
      'wav': 'audio/wav',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'ppt': 'application/vnd.ms-powerpoint',
      'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'csv': 'text/csv',
      'xml': 'application/xml',
      'zip': 'application/zip',
      'rar': 'application/x-rar-compressed',
      'tar': 'application/x-tar',
      'gz': 'application/gzip',
      'md': 'text/markdown'
    };
    
    return mimeTypes[extension] || 'application/octet-stream';
  }

  clearCache(assistantId?: string) {
    if (assistantId) {
      this.assistants.delete(assistantId);
      this.openaiClients.clear();
    } else {
      this.assistants.clear();
      this.openaiClients.clear();
    }
  }
}

export const openAIAssistantsService = new OpenAIAssistantsService();